// Confirmation dialog shown after the user drops files (or picks
// them via the native file picker). Lists each file with size and
// detected MIME. Clicking Upload closes the dialog immediately and
// starts runImport() for each file in the background — the floating
// Transfers panel is the authoritative progress surface from that
// point on.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { enqueueUpload, runImport } from "../../lib/uploadQueue";

const IMAGE_MIME = /^image\/(jpeg|png|webp|gif|avif|heic|heif)$/i;

interface UploadConfirmDialogProps {
  files: File[];
  folderPath: string;
  onCancel: () => void;
  // Called the moment the user clicks Upload; uploads continue in
  // the background after this fires.
  onStarted: () => void;
  // Called once every queued upload has reached a terminal state.
  onAllDone: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

export function UploadConfirmDialog({
  files,
  folderPath,
  onCancel,
  onStarted,
  onAllDone,
}: UploadConfirmDialogProps) {
  const worker = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);

  const total = files.reduce((acc, f) => acc + f.size, 0);

  // Blob-URL previews for image files. Created inside useEffect so
  // each mount gets fresh URLs paired with their own cleanup — in
  // React 18 StrictMode dev, components unmount and remount once;
  // creating URLs outside the effect meant the cleanup revoked the
  // live URLs that the remounted tree still pointed at.
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([]);
  useEffect(() => {
    const urls = files.map((f) =>
      IMAGE_MIME.test(f.type) ? URL.createObjectURL(f) : null,
    );
    setPreviewUrls(urls);
    return () => {
      for (const url of urls) {
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, [files]);

  const startUpload = () => {
    if (handle === null) {
      toast.error("Vault is locked.");
      return;
    }
    // Enqueue every file synchronously before we close the dialog so
    // the Transfers panel shows the full batch immediately, with all
    // pending rows in `queued` state. The detached loop below then
    // promotes them to `running` one at a time.
    const taskIds = files.map((f) => enqueueUpload(f));
    // Close the dialog immediately — the floating Transfers panel
    // owns progress display from here on. The upload loop runs as a
    // detached promise so we never await it inside the dialog.
    onStarted();
    void (async () => {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const taskId = taskIds[i];
        try {
          await runImport(worker, handle, folderPath, file, taskId);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          toast.error(`Failed to upload ${file.name}: ${msg}`);
        }
      }
      onAllDone();
    })();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Upload {files.length} file{files.length === 1 ? "" : "s"}?
          </DialogTitle>
          <DialogDescription>
            Files will be encrypted inside the vault. The storage
            backend never sees plaintext or filenames.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-60 overflow-auto rounded-md border border-border-soft">
          <ul className="divide-y divide-border-soft">
            {files.map((f, i) => {
              const preview = previewUrls[i];
              return (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-3 px-3 py-2 text-sm"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt=""
                      aria-hidden
                      className="h-8 w-8 flex-shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-bg-sidebar text-[9px] font-mono text-text-dim">
                      {(f.name.split(".").pop() ?? "").slice(0, 3).toUpperCase() || "FILE"}
                    </div>
                  )}
                  <span
                    className="flex-1 truncate font-medium text-text"
                    title={f.name}
                  >
                    {f.name}
                  </span>
                  <span className="flex-shrink-0 text-xs text-text-dim">
                    {formatBytes(f.size)}
                    {f.type ? ` · ${f.type}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between text-xs text-text-dim">
          <span>
            Destination: <span className="font-mono text-text-soft">{folderPath}</span>
          </span>
          <span>Total: {formatBytes(total)}</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={startUpload} disabled={handle === null}>
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
